import { useEffect, useMemo, useState, useRef } from "react";
import Select from "react-select";
import {
  Search,
  RotateCw,
  Plus,
  BookOpen,
  GraduationCap,
  X,
  FileSpreadsheet,
} from "lucide-react";

import customSelectStyles from "../../../components/ui/customSelectStyles";
import { getLessonCourses } from "../../../api/elearning/lessons/lessons";
import { hasPermission } from "../../../utils/permissionUtils";

const DEFAULT_FILTERS = {
  keyword: "",
  subject_id: "",
  course_id: "",
};

/** Đồng bộ nền/viền với ô tìm kiếm + nút refresh */
const FILTER_CONTROL = {
  bg: "#ffffff",
  border: "#e7e5e4", // stone-200
  borderFocus: "#f87171", // red-400
  radius: "0.75rem", // rounded-xl
  height: 44,
};

const filterSelectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...provided,
    backgroundColor: state.isDisabled ? "#fafafa" : FILTER_CONTROL.bg,
    borderColor: state.isFocused
      ? FILTER_CONTROL.borderFocus
      : FILTER_CONTROL.border,
    borderWidth: "1px",
    borderRadius: FILTER_CONTROL.radius,
    minHeight: FILTER_CONTROL.height,
    height: FILTER_CONTROL.height,
    boxShadow: "none",
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    transition: "border-color 0.15s ease, background-color 0.15s ease",
    "&:hover": {
      borderColor: state.isFocused
        ? FILTER_CONTROL.borderFocus
        : "#d6d3d1", // stone-300
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: FILTER_CONTROL.height - 2,
    padding: "0 12px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: FILTER_CONTROL.height - 2,
  }),
  placeholder: (base) => ({
    ...base,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#a8a29e",
  }),
  singleValue: (base) => ({
    ...base,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const fieldClass =
  "h-11 w-full rounded-xl border border-stone-200 bg-white text-sm font-medium text-stone-800 outline-none transition-colors placeholder:text-stone-400 focus:border-red-400 disabled:cursor-not-allowed disabled:bg-stone-50 disabled:text-stone-400";

const iconBtnClass =
  "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-500 transition-colors hover:border-stone-300 hover:text-red-600";

const LessonFilter = ({
  onNewClick,
  onImportExcelClick,
  onFilterChange,
  reloadKey = 0,
  activeFilters = null,
}) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  useEffect(() => {
    if (activeFilters) {
      setFilters((prev) => ({ ...prev, ...activeFilters }));
    }
  }, [activeFilters]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCourses(true);
      try {
        const res = await getLessonCourses({ has_lessons: "true" });
        if (!cancelled) setCourses(Array.isArray(res) ? res : []);
      } catch (err) {
        console.error("Lỗi tải khóa học", err);
        if (!cancelled) setCourses([]);
      } finally {
        if (!cancelled) setLoadingCourses(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const keywordBoot = useRef(true);

  useEffect(() => {
    if (keywordBoot.current) {
      keywordBoot.current = false;
      return;
    }
    const timer = setTimeout(() => {
      onFilterChange?.(filters);
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ debounce theo keyword
  }, [filters.keyword]);

  const setSubject = (subjectId) => {
    const next = {
      ...filters,
      subject_id: subjectId || "",
      course_id: "",
    };
    setFilters(next);
    onFilterChange?.(next);
  };

  const setCourse = (courseId) => {
    const next = { ...filters, course_id: courseId || "" };
    setFilters(next);
    onFilterChange?.(next);
  };

  const clearKeyword = () => {
    const next = { ...filters, keyword: "" };
    setFilters(next);
    onFilterChange?.(next);
  };

  const handleKeywordChange = (value) => {
    setFilters((prev) => ({ ...prev, keyword: value }));
  };

  const handleRefresh = () => {
    onFilterChange?.({ ...filters });
  };

  // Chỉ lấy những khóa học có số bài học > 0
  const coursesWithLessons = useMemo(() => {
    return (courses || []).filter((c) => Number(c?.lesson_count || 0) > 0);
  }, [courses]);

  // Ô chọn Môn học: Chỉ hiển thị môn học có ít nhất 1 khóa học với bài học > 0
  const subjectOptions = useMemo(() => {
    const map = new Map();
    coursesWithLessons.forEach((c) => {
      if (!c?.subject_id) return;
      if (!map.has(c.subject_id)) {
        map.set(c.subject_id, {
          value: c.subject_id,
          label: c.subject_name || "Môn học",
          sort: Number(c.subject_sort_order ?? 0),
        });
      }
    });
    return [...map.values()].sort(
      (a, b) => a.sort - b.sort || a.label.localeCompare(b.label, "vi")
    );
  }, [coursesWithLessons]);

  useEffect(() => {
    if (loadingCourses) return;
    if (filters.subject_id) {
      const exists = subjectOptions.some((o) => String(o.value) === String(filters.subject_id));
      if (!exists) {
        setSubject("");
      }
    } else if (subjectOptions.length === 1) {
      setSubject(subjectOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCourses, subjectOptions]);

  const canCreate = hasPermission("elearning.lessons", "C");

  // Ô chọn Khóa học: Chỉ hiển thị khóa học có bài học > 0 thuộc môn học được chọn
  const courseOptions = useMemo(() => {
    if (!filters.subject_id) return [];
    return coursesWithLessons
      .filter((c) => String(c.subject_id) === String(filters.subject_id))
      .map((c) => ({
        value: c.id,
        label: c.name,
        lessonCount: Number(c.lesson_count || 0),
      }));
  }, [coursesWithLessons, filters.subject_id]);

  useEffect(() => {
    if (!filters.course_id) return;
    const exists = courseOptions.some((o) => String(o.value) === String(filters.course_id));
    if (!exists) {
      setCourse("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseOptions]);

  const hasKeyword = Boolean(filters.keyword?.trim());
  const subjectSelected = Boolean(filters.subject_id);

  return (
    <div className="mb-6 rounded-2xl border border-red-100/80 bg-white/90 p-4 text-left shadow-sm shadow-red-100/40 backdrop-blur-sm sm:p-5">
      <div className="space-y-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-end lg:gap-3">
          <div
            data-tour="lesson-subject"
            className="w-full min-w-0 lg:w-[300px] lg:shrink-0 xl:w-[340px]"
          >
            <label className="mb-2 ml-1 flex items-center gap-2 text-[11px] font-black uppercase text-stone-400">
              <GraduationCap size={14} className="shrink-0 text-red-500" />
              Môn học <span className="text-red-600">*</span>
            </label>
            <Select
              styles={filterSelectStyles}
              options={subjectOptions}
              placeholder={loadingCourses ? "Đang tải…" : "Chọn môn học…"}
              isLoading={loadingCourses}
              isDisabled={loadingCourses || subjectOptions.length === 0}
              value={
                subjectOptions.find((o) => o.value === filters.subject_id) ||
                null
              }
              onChange={(opt) => setSubject(opt?.value || "")}
              isClearable
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
            />
          </div>

          <div
            data-tour="lesson-course"
            className="w-full min-w-0 lg:w-[360px] lg:shrink-0 xl:w-[420px]"
          >
            <label className="mb-2 ml-1 flex items-center gap-2 text-[11px] font-black uppercase text-stone-400">
              <BookOpen size={14} className="shrink-0 text-red-500" />
              Khóa học <span className="text-red-600">*</span>
            </label>
            <Select
              styles={filterSelectStyles}
              options={courseOptions}
              placeholder={
                !subjectSelected
                  ? "Chọn môn học trước…"
                  : courseOptions.length === 0
                    ? "Không có khóa học"
                    : "Chọn khóa học…"
              }
              isDisabled={!subjectSelected}
              value={
                courseOptions.find((o) => o.value === filters.course_id) || null
              }
              onChange={(opt) => setCourse(opt?.value || "")}
              formatOptionLabel={(option, { context, selectValue }) => {
                if (context === "value") return option.label;
                const isSelected = Array.isArray(selectValue)
                  ? selectValue.some((v) => v?.value === option.value)
                  : false;
                return (
                  <div className="py-0.5 leading-tight">
                    <div
                      className="font-semibold"
                      style={{ color: isSelected ? "#ffffff" : "#1f2937" }}
                    >
                      {option.label}
                    </div>
                    <div
                      className="mt-0.5 text-[11px]"
                      style={{
                        color: isSelected
                          ? "rgba(255, 255, 255, 0.92)"
                          : "#6b7280",
                      }}
                    >
                      {option.lessonCount} bài học
                    </div>
                  </div>
                );
              }}
              isClearable
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
            />
          </div>

          <div
            className="flex w-full items-end gap-2 lg:min-w-[240px] lg:flex-1 xl:w-[380px] xl:flex-none"
            data-tour="lesson-toolbar"
          >
            <div className="min-w-0 flex-1">
              <label className="mb-2 ml-1 block text-[11px] font-black uppercase text-stone-400">
                Tìm kiếm
              </label>
              <div
                className="group relative"
                data-tour="lesson-search"
              >
                <Search
                  className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 transition-colors group-focus-within:text-red-500"
                  size={18}
                />
                <input
                  type="text"
                  placeholder="Tìm theo tiêu đề…"
                  value={filters.keyword}
                  onChange={(e) => handleKeywordChange(e.target.value)}
                  className={`${fieldClass} pl-11 pr-10`}
                />
                {hasKeyword ? (
                  <button
                    type="button"
                    title="Xóa từ khóa"
                    onClick={clearKeyword}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-stone-400 hover:bg-stone-100 hover:text-stone-600"
                  >
                    <X size={16} />
                  </button>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              title="Làm mới danh sách"
              onClick={handleRefresh}
              className={iconBtnClass}
            >
              <RotateCw size={18} />
            </button>
          </div>

          {canCreate ? (
            <div
              className="hidden shrink-0 items-end gap-2 lg:ml-auto lg:flex"
              data-tour="lesson-toolbar-desktop"
            >
              {onImportExcelClick ? (
                <button
                  type="button"
                  onClick={onImportExcelClick}
                  className="inline-flex h-11 items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 text-xs font-bold uppercase tracking-wide text-red-700 transition-all hover:bg-red-100 active:scale-[0.98]"
                >
                  <FileSpreadsheet size={18} />
                  Nhập Excel
                </button>
              ) : null}
              <button
                type="button"
                onClick={onNewClick}
                className="inline-flex h-11 items-center gap-2 rounded-xl bg-gradient-to-r from-red-700 to-red-800 px-5 text-xs font-bold uppercase tracking-wide text-white transition-all hover:shadow-lg hover:shadow-red-100/50 active:scale-[0.98]"
              >
                <Plus size={18} />
                Thêm bài học
              </button>
            </div>
          ) : null}
        </div>

        <p className="ml-1 text-xs text-stone-400">
          Chọn môn học → khóa học để xem, tải và trình chiếu bài giảng. Dùng
          «Nhập Excel» để tạo hàng loạt bài học (metadata; tài liệu gắn sau).
        </p>
      </div>
    </div>
  );
};

export default LessonFilter;
